import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Check, Copy, MessageCircle, Trash2 } from "lucide-react";
import {
  ANDROID_TESTERS_NEEDED,
  ANDROID_TEST_DAYS,
  ANDROID_TEST_REMIND_EVERY,
  displayPhone,
  type AndroidTesterMessageResponse,
  type AndroidTestersResponse,
} from "@mubin/contracts";
import { api, errorText, send } from "../lib/api";
import { Badge, Empty, ErrorNote, Field, Loading, useSubmit } from "./ui";
import { PhoneField } from "./phone-field";

/**
 * Testers of Google Play's closed test (owner request 2026-10-25): who signed up on /android-test and how far
 * they got, their emails to paste into Play, and the link and demo account the tester page shows.
 */
export function AndroidTestersCard({ organizationId }: { organizationId: string }) {
  const qc = useQueryClient();
  const key = ["android-testers", organizationId];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api<AndroidTestersResponse>(`/v1/android-testers?organizationId=${organizationId}`),
  });
  const save = useSubmit();
  const remove = useSubmit();
  const act = useSubmit();
  const waTest = useSubmit();
  const [form, setForm] = useState({
    joinUrl: "",
    demoPhone: "",
    demoPassword: "",
    waBaseUrl: "",
    waInstanceId: "",
    waToken: "",
    waActive: false,
  });
  const [note, setNote] = useState<string | null>(null);
  const [testPhone, setTestPhone] = useState("");
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const pageUrl = `${window.location.origin}/android-test`;

  useEffect(() => {
    const s = list.data?.settings;
    if (s)
      setForm({
        joinUrl: s.joinUrl ?? "",
        demoPhone: s.demoPhone ?? "",
        demoPassword: s.demoPassword ?? "",
        waBaseUrl: s.waBaseUrl ?? "",
        waInstanceId: s.waInstanceId ?? "",
        waToken: "",
        waActive: s.waActive,
      });
  }, [list.data?.settings]);

  function submit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    void save.run(async () => {
      await send("PUT", "/v1/android-test-settings", {
        organizationId,
        joinUrl: form.joinUrl.trim() || null,
        demoPhone: form.demoPhone.trim() || null,
        demoPassword: form.demoPassword || null,
        waBaseUrl: form.waBaseUrl.trim() || null,
        waInstanceId: form.waInstanceId.trim() || null,
        waToken: form.waToken.trim() || undefined,
        waActive: form.waActive,
      });
      await qc.invalidateQueries({ queryKey: key });
      setForm((f) => ({ ...f, waToken: "" }));
      setSaved(true);
    }, errorText);
  }

  /** Marks a tester added to Google Play, or reminds them of their next step; both send a WhatsApp message. */
  function message(id: string, name: string, what: "added" | "remind") {
    setNote(null);
    void act.run(async () => {
      const r = await send<AndroidTesterMessageResponse>(
        "POST",
        `/v1/android-testers/${id}/${what}?organizationId=${organizationId}`,
      );
      await qc.invalidateQueries({ queryKey: key });
      setNote(r.sent ? `وصلت الرسالة إلى ${name}` : `لم تُرسل الرسالة إلى ${name}: ${r.error ?? ""}`);
    }, errorText);
  }

  const copy = (text: string, what: string) =>
    void navigator.clipboard?.writeText(text).then(() => setCopied(what));
  const testers = list.data?.testers ?? [];
  const installed = testers.filter((t) => t.installedAt).length;
  const when = (d: string | null) => (d ? new Date(d).toLocaleDateString("ar-SA") : "—");

  return (
    <section className="card">
      <div className="card-h">
        <h2>مختبرو Google Play</h2>
        <Badge tone={installed >= ANDROID_TESTERS_NEEDED ? "success" : "neutral"}>
          ثبّت {installed} من {ANDROID_TESTERS_NEEDED}
        </Badge>
      </div>
      <div className="section form">
        <p className="muted small">
          يشترط Google للحساب الشخصي الجديد اختبارًا مغلقًا مع {ANDROID_TESTERS_NEEDED} مختبرًا لمدة 14 يومًا،
          ونحسب {ANDROID_TEST_DAYS} يومًا احتياطًا. بعد آخر خطوة يصل المختبر تذكير كل{" "}
          {ANDROID_TEST_REMIND_EVERY} أيام ليفتح التطبيق ويؤكد بضغطة «فتحت». شارك صفحة التسجيل، ثم انسخ
          الإيميلات والصقها في قائمة المختبرين في Play Console.
        </p>
        <div className="row-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(pageUrl, "page")}>
            <Copy size={16} strokeWidth={1.75} />{" "}
            {copied === "page" ? "تم نسخ رابط الصفحة" : "نسخ رابط صفحة التسجيل"}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!testers.length}
            onClick={() => copy(testers.map((t) => t.email).join(", "), "emails")}
          >
            <Copy size={16} strokeWidth={1.75} />{" "}
            {copied === "emails" ? "تم نسخ الإيميلات" : `نسخ كل الإيميلات (${testers.length})`}
          </button>
        </div>
      </div>
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <Empty title="تعذر التحميل">{errorText(list.error)}</Empty>
      ) : testers.length === 0 ? (
        <Empty title="لم يسجل أحد بعد">شارك رابط صفحة التسجيل في قروب الحلقات.</Empty>
      ) : (
        <div className="table-wrap">
          <ErrorNote error={remove.error ?? act.error} />
          {note && <p className="notice small">{note}</p>}
          <table>
            <thead>
              <tr>
                <th>الاسم</th>
                <th>الإيميل</th>
                <th>الجوال</th>
                <th>أُضيف</th>
                <th>انضم</th>
                <th>ثبّت</th>
                <th>جرّب</th>
                <th>أيام التجربة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {testers.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="ltr small">{t.email}</td>
                  <td className="small">
                    {t.phone ? (
                      <a
                        className="ltr"
                        href={`https://wa.me/${t.phone.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <MessageCircle size={14} strokeWidth={1.75} /> {displayPhone(t.phone)}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="small">
                    {t.addedAt ? (
                      when(t.addedAt)
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={act.busy}
                        title="أضفت إيميله في Play Console: تصله رسالة برابط الانضمام"
                        onClick={() => message(t.id, t.name, "added")}
                      >
                        <Check size={14} strokeWidth={1.75} /> أضفته
                      </button>
                    )}
                  </td>
                  <td className="small">{when(t.joinedAt)}</td>
                  <td className="small">{when(t.installedAt)}</td>
                  <td className="small">{when(t.triedAt)}</td>
                  <td className="small">
                    {t.testDay === null ? (
                      "—"
                    ) : t.testDay > ANDROID_TEST_DAYS ? (
                      <Badge tone="success">انتهت · أكّد {t.openDays} مرات</Badge>
                    ) : (
                      <>
                        اليوم {t.testDay} من {ANDROID_TEST_DAYS} · أكّد {t.openDays}{" "}
                        {(t.idleDays ?? 0) > ANDROID_TEST_REMIND_EVERY && (
                          <Badge tone="danger">لم يؤكد منذ {t.idleDays} أيام</Badge>
                        )}
                      </>
                    )}
                  </td>
                  <td className="actions">
                    {t.addedAt && (!t.triedAt || (t.testDay ?? 0) <= ANDROID_TEST_DAYS) && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        aria-label={`تذكير ${t.name}`}
                        title={
                          t.triedAt ? "تذكير بفتح التطبيق عبر الواتساب" : "تذكير بالخطوة التالية عبر الواتساب"
                        }
                        disabled={act.busy}
                        onClick={() => message(t.id, t.name, "remind")}
                      >
                        <BellRing size={16} strokeWidth={1.75} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      aria-label={`حذف ${t.name}`}
                      disabled={remove.busy}
                      onClick={() =>
                        void remove.run(async () => {
                          await api(`/v1/android-testers/${t.id}?organizationId=${organizationId}`, {
                            method: "DELETE",
                          });
                          await qc.invalidateQueries({ queryKey: key });
                        }, errorText)
                      }
                    >
                      <Trash2 size={16} strokeWidth={1.75} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <form
        className="section form"
        onSubmit={submit}
        style={{ borderTop: "1px solid var(--border-default)" }}
      >
        <h3 className="small">ما يظهر للمختبرين في صفحة التسجيل</h3>
        <ErrorNote error={save.error} />
        <Field
          id="at-join"
          label="رابط الانضمام للاختبار المغلق"
          hint="من Play Console ← الاختبار المغلق ← المختبِرون"
        >
          <input
            id="at-join"
            type="url"
            dir="ltr"
            placeholder="https://play.google.com/apps/testing/app.mubin"
            value={form.joinUrl}
            onChange={(e) => setForm({ ...form, joinUrl: e.target.value })}
          />
        </Field>
        <div className="form-row">
          <Field id="at-phone" label="رقم الحساب التجريبي">
            <input
              id="at-phone"
              dir="ltr"
              value={form.demoPhone}
              onChange={(e) => setForm({ ...form, demoPhone: e.target.value })}
            />
          </Field>
          <Field id="at-pass" label="كلمة مرور الحساب التجريبي" hint="تظهر لكل من سجّل في الصفحة">
            <input
              id="at-pass"
              dir="ltr"
              value={form.demoPassword}
              onChange={(e) => setForm({ ...form, demoPassword: e.target.value })}
            />
          </Field>
        </div>
        <h3 className="small">واتساب فريق التجربة</h3>
        <p className="muted small">
          رقم مستقل عن واتساب الجمعية (مثل رقمك الشخصي حتى نشر التطبيق). تصل منه للمختبر رسالة عند التسجيل،
          وعند إضافته، وبعد كل خطوة، وعند التذكير. الرمز (Token) لا يُعرض بعد حفظه.
        </p>
        <div className="form-row">
          <Field id="at-wa-base" label="رابط الخدمة (Base URL)">
            <input
              id="at-wa-base"
              type="url"
              dir="ltr"
              value={form.waBaseUrl}
              onChange={(e) => setForm({ ...form, waBaseUrl: e.target.value })}
            />
          </Field>
          <Field id="at-wa-inst" label="معرّف الحساب (Instance ID)">
            <input
              id="at-wa-inst"
              dir="ltr"
              value={form.waInstanceId}
              onChange={(e) => setForm({ ...form, waInstanceId: e.target.value })}
            />
          </Field>
        </div>
        <Field
          id="at-wa-token"
          label="الرمز (Token)"
          hint={list.data?.settings.hasWaToken ? "محفوظ. اتركه فارغًا لإبقائه" : undefined}
        >
          <input
            id="at-wa-token"
            dir="ltr"
            autoComplete="off"
            placeholder={list.data?.settings.hasWaToken ? "••••••••" : ""}
            value={form.waToken}
            onChange={(e) => setForm({ ...form, waToken: e.target.value })}
          />
        </Field>
        <label className="check">
          <input
            type="checkbox"
            checked={form.waActive}
            onChange={(e) => setForm({ ...form, waActive: e.target.checked })}
          />
          مفعّل (عند التعطيل لا تُرسل رسائل للمختبرين)
        </label>
        <div className="form-actions">
          {saved && <span className="muted small">تم الحفظ</span>}
          <button className="btn btn-primary" disabled={save.busy}>
            {save.busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
        </div>
      </form>
      {list.data?.settings.hasWaToken && list.data.settings.waActive && (
        <div className="section form" style={{ borderTop: "1px solid var(--border-default)" }}>
          <h3 className="small">رسالة اختبار من واتساب فريق التجربة</h3>
          <ErrorNote error={waTest.error} />
          <div className="form-row" style={{ alignItems: "end" }}>
            <Field id="at-wa-test" label="أرسل إلى رقم">
              <PhoneField id="at-wa-test" value={testPhone} onChange={setTestPhone} />
            </Field>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={waTest.busy || testPhone.replace(/\D/g, "").length < 9}
              onClick={() =>
                void waTest.run(async () => {
                  const r = await send<AndroidTesterMessageResponse>(
                    "POST",
                    "/v1/android-test-settings/whatsapp-test",
                    {
                      organizationId,
                      phone: testPhone.trim(),
                    },
                  );
                  setNote(r.sent ? "وصلت رسالة الاختبار" : `فشل الإرسال: ${r.error ?? ""}`);
                }, errorText)
              }
            >
              {waTest.busy ? "جارٍ الإرسال…" : "إرسال رسالة اختبار"}
            </button>
          </div>
          {note && <p className="notice small">{note}</p>}
        </div>
      )}
    </section>
  );
}
